import React, { useEffect, useState } from 'react'
import { GetStaticProps, InferGetStaticPropsType } from 'next'
import Link from 'next/link'
import Image from 'next/image'
import { HiOutlineArrowNarrowRight } from 'react-icons/hi'
import { FiGithub, FiInstagram, FiLinkedin } from 'react-icons/fi'

import { Slider1, Slider2 } from '@/services/slider-data'
import api from '@/services/api'

import SEO from '@/components/SEO'
import Swiper, { SwiperSlide } from '@/components/Swiper'
import Button from '@/components/Button'
import { OptimizedAsset } from '@/components/OptimizedAsset'

import { Container } from '@/styles/pages/Home'
import createOgImage from '@/lib/createOgImage'
import resolveRepoPreview from '@/lib/resolveRepoPreview'
import repoSlug from '@/lib/repoSlug'
import { Repo } from '@/@types/@repo'

interface HomeStaticProps {
  ogImage: string
  repos: Repo[]
}

type HomeProps = InferGetStaticPropsType<GetStaticProps<HomeStaticProps>>

type HomeShowcaseCard = {
  title: string
  description: string
  href: string
  external?: boolean
  repoNames?: string[]
}

type CompanyExperience = {
  name: string
  role: string
}

type PublishedApp = {
  name: string
  role: string
  playUrl: string
  appStoreUrl: string
}

const FEATURED_SHOWCASE: HomeShowcaseCard[] = [
  {
    title: 'Papel-amassado.com',
    description:
      'Plataforma de leitura e escrita com foco em experiência limpa e conteúdo.',
    href: 'https://papel-amassado.com/',
    external: true
  },
  {
    title: 'Jarvis Dev',
    description: 'Assistente de produtividade para dev com foco em fluxo real.',
    href: 'https://www.jarvis-dev.com/',
    external: true,
    repoNames: ['Jarvis Dev']
  },
  {
    title: 'PacketShield',
    description:
      'Firewall inteligente para Windows com regras granulares por app e domínio.',
    href: 'https://packetshield.solrachix.com/',
    external: true,
    repoNames: ['PacketShield']
  },
  {
    title: 'Papyrus SDK',
    description:
      'SDK open source de PDF/EPUB/TXT para web e mobile com engines plugáveis.',
    href: 'https://solrachix.github.io/Papyrus/',
    external: true,
    repoNames: ['Papyrus PDF SDK']
  }
]

const OPEN_SOURCE_BASE: HomeShowcaseCard[] = [
  {
    title: 'Papyrus SDK',
    description: 'SDK modular para renderização de documentos no web e mobile.',
    href: 'https://solrachix.github.io/Papyrus/',
    external: true,
    repoNames: ['Papyrus PDF SDK']
  },
  {
    title: 'OrbitShell',
    description: 'Terminal moderno em blocos, com foco em produtividade.',
    href: '/projects/orbitshell',
    repoNames: ['OrbitShell']
  },
  {
    title: 'PacketShield',
    description:
      'Proteção de rede com controle por app/domínio e observabilidade local.',
    href: 'https://packetshield.solrachix.com/',
    external: true,
    repoNames: ['PacketShield']
  }
]

const PROFESSIONAL_EXPERIENCE: CompanyExperience[] = [
  {
    name: 'Sympla',
    role: 'Senior Frontend / Full-Stack — foco em produção, escala e jornada de compra.'
  },
  {
    name: 'MedCof',
    role: 'Tech Lead (Full-Stack) — plataforma de aulas e app MedCof Aulas.'
  },
  {
    name: 'Consigaz',
    role: 'Tech Lead + Dev — app publicado para Android e iOS.'
  },
  {
    name: 'Linx',
    role: 'Software Engineer / Full-Stack — evolução de produto e integrações.'
  }
]

const PUBLISHED_APPS: PublishedApp[] = [
  {
    name: 'MedCof Aulas',
    role: 'Tech Lead + Dev',
    playUrl:
      'https://play.google.com/store/apps/details?hl=pt&id=br.com.medcof.cursos',
    appStoreUrl: 'https://apps.apple.com/br/app/medcof-aulas/id6742911494'
  },
  {
    name: 'App Consigaz',
    role: 'Tech Lead + Dev',
    playUrl:
      'https://play.google.com/store/apps/details?hl=pt&id=com.semantix.consigaz',
    appStoreUrl: 'https://apps.apple.com/br/app/app-consigaz/id6449132679'
  }
]

const normalizeName = (value: string): string => value.trim().toLowerCase()

const findRepoByNames = (repos: Repo[], names?: string[]): Repo | null => {
  if (!names || names.length === 0) return null

  const byExact = repos.find(repo =>
    names.some(name => normalizeName(repo.name) === normalizeName(name))
  )

  if (byExact) return byExact

  return (
    repos.find(repo =>
      names.some(name => normalizeName(repo.name).includes(normalizeName(name)))
    ) || null
  )
}

const isRepoPublic = (repo: Repo | null): boolean => {
  if (!repo) return false
  return repo.private === false || repo.private === 0
}

const getProjectHref = (repo: Repo): string =>
  `/projects/${repo.slug || repoSlug(repo.name)}`

export default function Home(props: HomeProps) {
  const [slidesPerView, setSlidesPerView] = useState(3.5)
  const [spaceBetween, setSpaceBetween] = useState(50)
  const [slidesOffsetAfter, setSlidesOffsetAfter] = useState(250)

  const featuredCards = FEATURED_SHOWCASE.map(card => {
    const repo = findRepoByNames(props.repos, card.repoNames)
    const preview = repo ? resolveRepoPreview(repo) : null

    return {
      ...card,
      preview
    }
  })

  const openSourceCards = OPEN_SOURCE_BASE.map(card => {
    const repo = findRepoByNames(props.repos, card.repoNames)
    const preview = repo ? resolveRepoPreview(repo) : null

    const href = repo && !card.external ? getProjectHref(repo) : card.href

    return {
      ...card,
      href,
      preview,
      repo
    }
  }).filter(card => {
    if (card.title !== 'PacketShield') return true
    return isRepoPublic(card.repo)
  })

  useEffect(() => {
    const updateCarouselViewport = function () {
      const width = window.innerWidth

      if (width <= 600) {
        setSlidesPerView(2)
        setSpaceBetween(24)
        setSlidesOffsetAfter(0)
        return
      }

      if (width <= 900) {
        setSlidesPerView(2)
        setSpaceBetween(32)
        setSlidesOffsetAfter(100)
        return
      }

      setSlidesPerView(3)
      setSpaceBetween(50)
      setSlidesOffsetAfter(250)
    }

    const onResize = updateCarouselViewport.debounce(250)

    updateCarouselViewport()
    window.addEventListener('resize', onResize)

    return () => {
      window.removeEventListener('resize', onResize)
    }
  }, [])

  return (
    <Container>
      <SEO
        title="Carlos Miguel"
        description="Programador FullStack UI/UX Designer, apaixonado pelas melhores tecnologias de desenvolvimento back-end, front-end e mobile. Fascinado por aprendizado e por JavaScript."
        image={props.ogImage}
      />

      {/* <BackToTop /> */}

      <div className="header">
        <div className="title-box">
          <h1>Carlos</h1>
          <span>Especialista em desenvolvimento FullStack</span>

          <Link
            href="https://www.instagram.com/solrachix"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              type="button"
              text="Converse comigo"
              prefixIcon={FiInstagram}
            />
          </Link>

          <div className="header-social">
            <a
              href="https://github.com/solrachix"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub"
            >
              <FiGithub size={20} />
            </a>
            <a
              href="https://www.linkedin.com/in/carlos-miguel-380413197"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
            >
              <FiLinkedin size={20} />
            </a>
          </div>
        </div>

        <div className="HomeMockups__Wrapper">
          <div className="mockup image-1">
            <img src="/images/mockups/mockup-card2.svg" alt="Mockup card 2" />
          </div>
          <div className="mockup image-2">
            <img src="/images/mockups/mockup-card2.svg" alt="Mockup card 2" />
          </div>
          <div className="mockup image-3">
            <img
              src="/images/mockups/mockup-content.svg"
              alt="Mockup content"
            />
          </div>
          <div className="mockup image-4">
            <img src="/images/mockups/mockup2-bg.svg" alt="Mockup card 2" />
          </div>
          <div className="mockup image-5">
            <img src="/images/mockups/mockup3-bg.svg" alt="Mockup card 2" />
          </div>
        </div>
      </div>

      <section className="bio">
        <div className="row">
          <div className="col">
            <h2>Eu amo criar algo simples e limpo</h2>
          </div>
          <div className="col">
            <p>
              Um programador de interfaces e sistemas, atualmente morando em São
              Paulo. Gosta de explorar designs minimalistas e com boa
              funcionalidade.
            </p>
            <p>
              Pode passar horas todos os dias programando ou explorando designs.
              Você pode ver algumas dos meu design no{' '}
              <a href="http://instagram.com/solrachix">Instagram</a>.
            </p>
            <p>
              Também gosto de escrever dicas, a maior parte da minha dicas é
              sobre programação, podem todas ser encontradas no meu{' '}
              <a href="http://instagram.com/solrachix">Instagram</a> também.
            </p>
            <p>
              Sou ativo no instagram, se você tiver alguma dúvida não hesite em
              me mencionar ou chamar no direct, com certeza será um bate-papo
              interessante.
            </p>

            <div className="proof-card">
              <strong>Apps publicados</strong>
              <h4>2 apps publicados somando 110 mil+ downloads</h4>

              <ul>
                {PUBLISHED_APPS.map(app => (
                  <li key={`${app.name}-proof`}>
                    {app.name} — {app.role}
                  </li>
                ))}
              </ul>

              <div className="proof-links">
                {PUBLISHED_APPS.map(app => (
                  <a
                    key={`${app.name}-play`}
                    href={app.playUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {app.name} no Play
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <nav className="home-tabs" aria-label="Navegação da home">
        <a href="#projetos-destaque">Projetos em destaque</a>
        <a href="#open-source">Open Source</a>
        <a href="#experiencia-profissional">Experiência profissional</a>
        <a href="#apps-publicados">Apps publicados</a>
      </nav>

      <section id="projetos-destaque" className="featured top-section">
        <div className="featured-header">
          <div>
            <span>Projetos em destaque</span>
            <h3>Produtos em produção hoje</h3>
          </div>
          <Link href="/projects" className="featured-link">
            Ver todos os projetos
            <HiOutlineArrowNarrowRight />
          </Link>
        </div>

        <div className="featured-grid">
          {featuredCards.map(card => (
            <a
              key={card.title}
              href={card.href}
              className="featured-card"
              target={card.external ? '_blank' : undefined}
              rel={card.external ? 'noopener noreferrer' : undefined}
            >
              <div className="text">
                <h4>{card.title}</h4>
                <p>{card.description}</p>
                <span>
                  Acessar <HiOutlineArrowNarrowRight />
                </span>
              </div>
              {card.preview && (
                <div
                  className={`preview ${
                    card.preview.mode === 'logo' ? 'is-logo' : ''
                  }`}
                  style={{ backgroundImage: `url(${card.preview.url})` }}
                  aria-hidden="true"
                />
              )}
            </a>
          ))}
        </div>
      </section>

      <section id="open-source" className="top-section open-source">
        <div className="section-heading">
          <span>Open Source</span>
          <h3>Projetos públicos e manutenção ativa</h3>
        </div>

        <div className="open-source-grid">
          {openSourceCards.map(card => {
            const content = (
              <>
                <div className="text">
                  <h4>{card.title}</h4>
                  <p>{card.description}</p>
                  <span>
                    Abrir <HiOutlineArrowNarrowRight />
                  </span>
                </div>
                {card.preview && (
                  <div
                    className={`preview ${
                      card.preview.mode === 'logo' ? 'is-logo' : ''
                    }`}
                    style={{ backgroundImage: `url(${card.preview.url})` }}
                    aria-hidden="true"
                  />
                )}
              </>
            )

            if (card.external) {
              return (
                <a
                  key={card.title}
                  href={card.href}
                  className="section-card"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {content}
                </a>
              )
            }

            return (
              <Link key={card.title} href={card.href} className="section-card">
                {content}
              </Link>
            )
          })}
        </div>
      </section>

      <section id="experiencia-profissional" className="top-section experience">
        <div className="section-heading">
          <span>Experiência profissional</span>
          <h3>Empresas e desafios de produto</h3>
        </div>

        <div className="experience-grid">
          {PROFESSIONAL_EXPERIENCE.map(company => (
            <article key={company.name} className="experience-card">
              <div className="company-mark">
                {company.name.slice(0, 3).toUpperCase()}
              </div>
              <h4>{company.name}</h4>
              <p>{company.role}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="apps-publicados" className="top-section apps">
        <div className="section-heading">
          <span>Apps publicados</span>
          <h3>2 apps publicados somando 110 mil+ downloads</h3>
        </div>

        <p className="apps-note">
          Android e iOS publicados. O número de downloads considera dados
          públicos do Google Play.
        </p>

        <div className="apps-grid">
          {PUBLISHED_APPS.map(app => (
            <article key={app.name} className="app-card">
              <h4>{app.name}</h4>
              <p>{app.role}</p>
              <div className="store-links">
                <a href={app.playUrl} target="_blank" rel="noopener noreferrer">
                  Google Play
                </a>
                <a
                  href={app.appStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  App Store
                </a>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="projects plant-manager">
        <span>Janeiro de 2021</span>
        <h3>Interface dark</h3>
        <p>
          Sempre gostei do modo Dark nas interfaces, seja no Whatsapp, ou até
          mesmo a interface escura do Discord. E Eu acho que muitos tem essa
          paixão que nem eu, então, porque não colocar modo Dark nas interface?
        </p>

        <div className="mockups desktop">
          <OptimizedAsset
            source="/images/mockups/podcastr-2.png"
            avif="/images/mockups/podcastr-2.avif"
            webp="/images/mockups/podcastr-2.webp"
            pictureClassName="mockup-asset mockup-asset-1"
            alt="podcastr dark mode"
          />
          <OptimizedAsset
            source="/images/mockups/podcastr-3.png"
            avif="/images/mockups/podcastr-3.avif"
            webp="/images/mockups/podcastr-3.webp"
            pictureClassName="mockup-asset mockup-asset-2"
            alt="podcastr dark mode"
          />
          <OptimizedAsset
            source="/images/mockups/podcastr-1.png"
            avif="/images/mockups/podcastr-1.avif"
            webp="/images/mockups/podcastr-1.webp"
            pictureClassName="mockup-asset mockup-asset-3"
            alt="podcastr dark mode"
          />
        </div>

        <div className="mockups mobile">
          <Swiper
            freeMode
            freeModeMomentum={false}
            resistance={false}
            spaceBetween={16}
            slidesPerView={1.3}
            slidesOffsetAfter={80}
            observer
            observeParents
            resizeObserver
          >
            <SwiperSlide>
              <OptimizedAsset
                source="/images/mockups/podcastr-2.png"
                avif="/images/mockups/podcastr-2.avif"
                webp="/images/mockups/podcastr-2.webp"
                alt="podcastr dark mode"
              />
            </SwiperSlide>
            <SwiperSlide>
              <OptimizedAsset
                source="/images/mockups/podcastr-3.png"
                avif="/images/mockups/podcastr-3.avif"
                webp="/images/mockups/podcastr-3.webp"
                alt="podcastr dark mode"
              />
            </SwiperSlide>
            <SwiperSlide>
              <OptimizedAsset
                source="/images/mockups/podcastr-1.png"
                avif="/images/mockups/podcastr-1.avif"
                webp="/images/mockups/podcastr-1.webp"
                alt="podcastr dark mode"
              />
            </SwiperSlide>
          </Swiper>
        </div>
      </section>

      <section className="landing-showcase">
        <div className="landing-head">
          <span className="landing-tag">Janeiro de 2021</span>
          <h3>Landing pages</h3>
        </div>
        <p className="landing-desc">
          Realmente gosto de explorar a página de destino. Principalmente a
          parte de customização do layout. Muito satisfeito se você puder fazer
          um layout único, fácil de ver, mas ainda assim claro as informações.
        </p>
        <div className="landing-grid">
          <figure className="landing-card">
            <div className="scroll-shot">
              <OptimizedAsset
                source="/images/mockups/landing-page.png"
                avif="/images/mockups/landing-page.avif"
                webp="/images/mockups/landing-page.webp"
                alt="landing page preview"
              />
            </div>
            <figcaption>Ignite</figcaption>
          </figure>
        </div>
      </section>

      <section className="forms-showcase">
        <div className="landing-head">
          <span className="landing-tag">Janeiro de 2021</span>
          <h3>Formulários de cadastro</h3>
        </div>
        <p className="landing-desc">
          Formulários de cadastro é algo em que os design/desenvolvedores
          parecem esquecerem, além de terem muitos que confundem os usuários e
          não seja agradável eu gosto de deixar o mais confortável possível.
        </p>
        <div className="landing-grid">
          <figure className="landing-card">
            <div className="scroll-shot">
              <OptimizedAsset
                source="/images/mockups/form-1.svg"
                avif="/images/mockups/form-1.avif"
                webp="/images/mockups/form-1.webp"
                alt="form template"
              />
            </div>
            <figcaption>Template de login</figcaption>
          </figure>
          <figure className="landing-card">
            <div className="scroll-shot">
              <img src="/images/mockups/form-2.svg" alt="form template" />
            </div>
            <figcaption>Template de cadastro</figcaption>
          </figure>
        </div>
      </section>

      <section className="projects legacy-ecoleta">
        <span>Maio de 2020</span>
        <div className="row row-title">
          <h3>Ecoleta</h3>
          <Image
            loading="lazy"
            src="/images/mockups/ecoleta-logo.svg"
            alt="Ecoleta logo"
            width={71}
            height={72}
          />
        </div>
        <p>
          Ecoleta é um projeto desenvolvido a partir da Semana Internacional do
          Meio Ambiente. O objetivo é conectar pessoas a empresas que coletam
          resíduos específicos, como lâmpadas, baterias, etc.
        </p>

        <span className="alert">
          Mova para o lado <HiOutlineArrowNarrowRight />
        </span>
        <Swiper
          freeMode
          freeModeMomentum={false}
          resistance={false}
          spaceBetween={spaceBetween}
          slidesPerView={slidesPerView}
          slidesOffsetAfter={slidesOffsetAfter}
          observer
          observeParents
          resizeObserver
        >
          {Slider1.map((asset, index) => (
            <SwiperSlide key={index}>
              <OptimizedAsset
                {...asset}
                alt={`Ecoleta photo ${index + 1}`}
                width={400}
                height={850}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </section>

      <section className="projects legacy-plant">
        <span>Abril de 2021</span>
        <div className="row row-title">
          <h3>Plant Manager</h3>
          <Image
            loading="lazy"
            src="/images/mockups/plantManager-logo.svg"
            alt="PlantManager logo"
            width={71}
            height={72}
          />
        </div>
        <p>
          O Plant Manager é um app para lembrar as pessoas de regar as suas
          plantinhas.
        </p>

        <span className="alert">
          Mova para o lado <HiOutlineArrowNarrowRight />
        </span>
        <Swiper
          freeMode
          freeModeMomentum={false}
          resistance={false}
          spaceBetween={spaceBetween}
          slidesPerView={slidesPerView}
          slidesOffsetAfter={slidesOffsetAfter}
          observer
          observeParents
          resizeObserver
        >
          {Slider2.map((asset, index) => (
            <SwiperSlide key={index}>
              <OptimizedAsset
                {...asset}
                alt={`plantManager photo ${index}`}
                width={350}
                height={750}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </section>
    </Container>
  )
}

export const getStaticProps: GetStaticProps<HomeStaticProps> = async () => {
  const ogImage = await createOgImage({
    title: 'Portfolio'
  })

  let repos: Repo[] = []

  try {
    const response = await api.get<Repo[]>('/repos', {
      params: {
        page: 0,
        type: 'FullStack,Web,Mobile,landing-page,Desktop,SDK'
      }
    })
    repos = response.data
  } catch (error) {
    repos = []
  }

  return {
    revalidate: 86400,
    props: {
      ogImage,
      repos
    }
  }
}
